import React, { ReactElement, useEffect, useState } from 'react';
import {
  Button,
  Col,
  CopyIcon,
  DeleteIcon,
  DescriptionOutlinedIcon,
  Divider,
  FileIcon,
  FormContainer,
  FormItem,
  Input,
  Modal,
  MoreHorizIcon,
  NativeSelect,
  Popover,
  Row,
  Select,
  Spinner,
  TextArea,
} from 'elmo-elements';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useUpdateElearningMutation,
  useDeleteElearningMutation,
  useDuplicateElearningMutation,
} from '@/modules/services/myElearnings';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';
import ColourPicker from '@/components/Form/ColourPicker';
import { ElearningDataType } from '@/modules/types/elearning';
import ContainerForm from '@/components/ContainerForm';
import CKFileUpload from '@/components/CKFileUpload/CKFileUpload';
import { CkFinderResourceTypeEnum } from '@/modules/types/ckFinder';
import './Form.scss';
import { getCkFileType, getCkFinderConfig, removeHashOnHex } from '@/lib/util';
import { useUserHasRole } from '@/lib/auth/useUserHasRoles';
import { Role } from '@/lib/auth/roles/role.enum';
import { generatePath, useHistory } from 'react-router-dom';
import MyLibraryRoutes from '@/pages/MyLibrary/MyLibraryRoutes';
import {
  useGetDefaultThemeQuery,
  useGetElearningCategoriesV2Query,
  useGetThemesQuery,
} from '@/modules/services';
import { MuteType } from '@/modules/types/elearning/muteType.enum';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { debounce } from 'lodash';
import ExportModuleDialog from '@/components/DocxPreviewModal/ExportModuleDialog';
import ElaPermissions from '@/lib/auth/permissions/permission.type';
import { useUserHasCapabilities } from '@/lib/auth/useUserHasCapabilities';
import CannotDeleteModuleModal from '@/pages/Module/Setup/CannotDeleteModuleModal';
import { ModuleDeletionBlocked } from '@/modules/types/elearning/moduleDeletionBlocked';
import { isModuleDeletionBlocked } from '@/modules/utils/isModuleDeletionBlocked';

interface ModuleSetupFormType {
  name: string;
  description?: string;
  useDefaultTheme: boolean;
  theme: {
    id: string;
  };
  categories: { label: string; value: string }[];
  playerMetadata: {
    showMenu: number;
    mute: MuteType;
  };
  titleMetadata: {
    posX: number;
    posY: number;
    nameFont: string;
    nameSize: string;
    nameColor: string;
  };
  bgImageName?: string;
  tags?: string;
  id: string;
}

interface TitleFontOptionsType {
  name: string;
  value: string;
}

const titleFontOptions: TitleFontOptionsType[] = [
  { name: 'Open Sans', value: 'Open Sans, sans-serif' },
  { name: 'Helvetica', value: 'helvetica,arial,sans-serif' },
  { name: 'Arial', value: 'arial,helvetica,sans-serif' },
  { name: 'Comic Sans MS', value: 'comic sans ms,sans-serif' },
  { name: 'Courier New', value: 'courier new, courier, monospace' },
  { name: 'Georgia', value: 'georgia,serif' },
  {
    name: 'Lucida Sans Unicode',
    value: 'lucida sans unicode,lucida grande,sans-serif',
  },
  { name: 'Tahoma', value: 'tahoma, sans-serif' },
  { name: 'Times New Roman', value: 'times new roman,times' },
  { name: 'Trebuchet MS', value: 'trebuchet ms,helvetica,sans-serif' },
  { name: 'Verdana', value: 'verdana' },
  { name: 'Poppins', value: 'Poppins, sans-serif' },
  { name: 'Aleo', value: 'Aleo, serif' },
  { name: 'Custom Font', value: 'customfont' },
];

const muteTypes = [
  { label: 'No', value: MuteType.NO },
  { label: 'Yes', value: MuteType.YES },
  { label: 'Hidden', value: MuteType.HIDDEN },
];

const schema = yup.object().shape({
  name: yup.string().max(255).trim().required('Module Name is required'),
  description: yup.string().trim(),
  useDefaultTheme: yup.boolean().default(false),
  theme: yup.object().shape({
    id: yup.lazy((value) => (!value ? yup.string() : yup.string().uuid())),
  }),
  categories: yup.array().of(
    yup.object().shape({
      label: yup.string().trim().required('Category label is required'),
      value: yup.string().trim().required('Category value is required'),
    }),
  ),
  titleMetadata: yup.object().shape({
    posX: yup
      .number()
      .typeError('PosX must be a number')
      .positive()
      .required('Pos X is required'),
    posY: yup
      .number()
      .typeError('PosY must be a number')
      .positive()
      .required('Pos Y is required'),
    nameFont: yup.string().max(255).required('Title font is required'),
    nameSize: yup
      .string()
      .max(50)
      .required('Title size is required')
      .notOneOf(['0em', '0px'], 'Title size cannot be 0'),
    nameColor: yup
      .string()
      .max(7, 'Need to be valid Hexidecimal')
      .required('Title Color is required')
      .matches(
        /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
        'Need to be valid Hexidecimal',
      ),
  }),
  playerMetadata: yup.object().shape({
    showMenu: yup
      .number()
      .min(0)
      .max(1)
      .required('Please select from the options'),
    mute: yup.number().min(0).max(2).required('Please select from the options'),
  }),
  bgImageName: yup.string().max(255).nullable(),
  id: yup.string().uuid().required(),
});

interface ModuleSetupFormPropsType {
  elearningData: ElearningDataType;
}

/* This will be converted into null when passed as value into themeId on form submit */
const defaultThemeValue = '';

export const ModuleSetupForm = ({
  elearningData,
}: ModuleSetupFormPropsType): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isElmoAdmin = useUserHasRole([Role.Admin, Role.Super, Role.Migration]);
  const canDelete = useUserHasCapabilities([
    ElaPermissions.DELETE_MODULE_SETTINGS_MODULE,
  ]);
  const canExportModuleToWordDocument = useUserHasRole([
    Role.Admin,
    Role.Super,
    Role.Migration,
  ]);

  const [pageTheme, setPageTheme] = useState<number>(1);
  const [pageCategory, setPageCategory] = useState<number>(1);
  const [searchCategory, setSearchCategory] = useState('');
  const [skipTheme, setSkipTheme] = useState<boolean>(false);
  const [skipCategory, setSkipCategory] = useState<boolean>(false);

  const [themesList, setThemesList] = useState<
    { value: string; label: string }[]
  >([]);
  const [categoriesList, setCategoriesList] = useState<
    { value: string; label: string }[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [blockedDeletion, setBlockedDeletion] =
    useState<ModuleDeletionBlocked | null>(null);
  // Bumping this remounts the "More" Popover, resetting its internal open state
  // to closed — the menu doesn't auto-close when a content item opens a modal.
  const [moreMenuKey, setMoreMenuKey] = useState<number>(0);
  const closeMoreMenu = () => setMoreMenuKey((key) => key + 1);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] =
    useState<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [isExportDocumentBusy, setIsExportDocumentBusy] =
    useState<boolean>(false);

  const { data: defaultTheme } = useGetDefaultThemeQuery();
  const { data: elaCategories, isLoading: categoriesIsLoading } =
    useGetElearningCategoriesV2Query(
      {
        page: pageCategory,
        order: 'ASC',
        sort: 'name',
        limit: 10,
        search: searchCategory,
      },
      { skip: skipCategory },
    );
  const { data: themes, isLoading: themesIsLoading } = useGetThemesQuery(
    {
      page: pageTheme,
    },
    { skip: skipTheme },
  );
  const [updateElearning, updateElearningStatus] = useUpdateElearningMutation();
  const [deleteElearning, deleteElearningStatus] = useDeleteElearningMutation();
  const [duplicateElearning, { isLoading: isDuplicateElearningLoading }] =
    useDuplicateElearningMutation();

  const getDefaultThemeOption = () => ({
    value: defaultThemeValue,
    label: `Default - ${defaultTheme?.name}`,
  });

  const getElearningThemeOption = ({
    elearningData: elearning,
  }: ModuleSetupFormPropsType): { value: string; label: string } => ({
    value: `${elearning.theme?.id}`,
    label: `${elearning.theme?.name}`,
  });

  const {
    learningElaCourseLibraryAdmin: isElaCourseLibraryAdminEnabled,
    learningElaExportToDocument,
    learningElaPreventModuleDeletionWhenLinked20260619:
      isPreventDeletionEnabled,
  } = useFlags();

  const isExportToDocumentEnabled =
    learningElaExportToDocument && canExportModuleToWordDocument;

  const defaultValues = {
    name: elearningData.name,
    description: elearningData.description,
    useDefaultTheme: elearningData?.theme
      ? !!elearningData.useDefaultTheme
      : true,
    theme: {
      id: elearningData.useDefaultTheme
        ? getDefaultThemeOption().value
        : getElearningThemeOption({ elearningData }).value,
    },
    categories: elearningData.categories?.map((category) => ({
      value: category.id,
      label: category.name,
    })),
    playerMetadata: {
      showMenu:
        elearningData.playerMetadata.showMenu == null
          ? 1
          : +elearningData.playerMetadata.showMenu,
      mute:
        elearningData.playerMetadata.mute == null
          ? 0
          : +elearningData.playerMetadata.mute,
    },
    titleMetadata: {
      posX: elearningData.titleMetadata.posX
        ? +elearningData.titleMetadata.posX
        : 570,
      posY: elearningData.titleMetadata.posY
        ? +elearningData.titleMetadata.posY
        : 36,
      nameFont: elearningData.titleMetadata.nameFont
        ? elearningData.titleMetadata.nameFont
        : 'Open Sans',
      nameSize: elearningData.titleMetadata.nameSize
        ? elearningData.titleMetadata.nameSize
        : '1.3em',
      nameColor:
        elearningData.titleMetadata.nameColor ||
        elearningData.theme?.windowMetadata.windowBgColor ||
        '',
    },
    bgImageName:
      elearningData.bgImageName == null ? '' : elearningData.bgImageName,
    tags: '',
    id: elearningData.id,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useForm<ModuleSetupFormType>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const watchThemeId = watch('theme.id');
  const watchCategories = watch('categories');

  const onSubmit = (data: ModuleSetupFormType) => {
    updateElearning({
      ...data,
      ...(data.theme.id === defaultThemeValue
        ? { theme: null }
        : { theme: { id: data.theme.id } }),
      titleMetadata: {
        ...data.titleMetadata,
        nameColor: removeHashOnHex(data.titleMetadata.nameColor),
      },
      categories: data.categories.map((category) => ({
        id: category.value,
        name: category.label,
      })),
    } as ElearningDataType);
  };

  const handleValueTargetChange = (e: any, name: string) => {
    setValue(name as keyof ModuleSetupFormType, e.target.value);
  };

  const handleValueChange = (e: any, name: string) => {
    setValue(name as keyof ModuleSetupFormType, e);
  };

  const handleDeleteModule = (elearningId: string) => {
    // Keep the confirmation modal open while the request is in flight; it is
    // closed once the request settles (see the deleteElearningStatus effect).
    deleteElearning(elearningId);
  };

  const handleManageAssets = () => {
    const { connectorInfo, connectorPath } = getCkFinderConfig({
      tenantId: elearningData.tenantId,
      entityId: elearningData.id,
      folderName: 'elearnings',
    });

    CKFinder.popup({
      connectorPath,
      connectorInfo,
      chooseFiles: false,
      width: 800,
      height: 600,
    });
  };

  const handleDuplicateModule = async (elearningId: string) => {
    try {
      const duplicatedModule = await duplicateElearning(elearningId).unwrap();

      history.push(`/module/${duplicatedModule.id}/setup`);
      setIsDuplicateModalOpen(false);
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Elearning duplicated successfully',
        },
      });
    } catch (error) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }
  };

  useEffect(() => {
    if (deleteElearningStatus.isLoading) return;

    if (deleteElearningStatus.isSuccess) {
      // Request settled successfully — close the confirmation modal.
      setIsModalOpen(false);
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Delete module Successful!',
        },
      });
      // redirect to my-library after deleting module
      history.replace(generatePath(MyLibraryRoutes.path));
    }
    if (deleteElearningStatus.isError) {
      // Request settled with an error — close the confirmation modal.
      setIsModalOpen(false);
      // Deletion blocked because the module is still linked to active courses:
      // open the "Cannot Delete Module" modal instead of the generic failure flash.
      if (
        isPreventDeletionEnabled &&
        isModuleDeletionBlocked(deleteElearningStatus.error)
      ) {
        setBlockedDeletion(deleteElearningStatus.error.data);
        return;
      }
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Delete module Failed! Something went wrong.',
        },
      });
    }
  }, [deleteElearningStatus]);

  useEffect(() => {
    if (
      updateElearningStatus.isSuccess &&
      !updateElearningStatus.isError &&
      updateElearningStatus.data
    ) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Elearning Saved/ Updated Successfully',
          className: 'save-elearning-settings',
        },
      });
    }

    if (updateElearningStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
          className: 'save-elearning-settings-failed',
        },
      });
    }
  }, [updateElearningStatus]);

  useEffect(() => {
    if (themesIsLoading) return;
    if (!themes?.data.length) {
      setSkipTheme(true);
      return;
    }

    const result = themes?.data.map((theme) => ({
      value: `${theme.id}`,
      label: `${theme.name}`,
    }));
    setThemesList([...themesList, ...result]);
  }, [themes?.data]);

  useEffect(() => {
    if (categoriesIsLoading) return;
    if (!elaCategories?.data.length) {
      setSkipCategory(true);
      return;
    }

    if (elaCategories?.data) {
      const result = elaCategories.data.map((category) => ({
        value: category.id,
        label: category.name,
      }));

      setCategoriesList((prev) => {
        if (pageCategory === 1) {
          return result;
        }
        return [...prev, ...result];
      });
    }
  }, [elaCategories?.data]);

  const handleCategorySearch = debounce((value: string) => {
    setSearchCategory(value);
    setPageCategory(1);
    setSkipCategory(false);
  }, 300);

  const handleImgUpload = (currentFileName: string) => {
    setValue('bgImageName', currentFileName);
  };

  const handleThemeChange = (theme: { value: string; label: string }) => {
    handleValueChange(!theme.value, 'useDefaultTheme');
    handleValueChange(theme.value, 'theme.id');
  };

  const getSelectedThemeValue = (id: string) =>
    themesList.find((theme) => theme.value === id);

  const handleCategoryChange = (
    categories: { value: string; label: string }[],
  ) => {
    setValue('categories' as keyof ModuleSetupFormType, categories);
  };

  if (updateElearningStatus.isLoading || isElmoAdmin === undefined) {
    return <Spinner size="xl" ariaLabel="Saving in progress" />;
  }

  return (
    <>
      <Modal
        id="DeleteModuleModalDialog"
        type="small"
        isOpened={isModalOpen}
        closeModal={() => {
          // Block closing while the delete request is in flight.
          if (!deleteElearningStatus.isLoading) {
            setIsModalOpen(false);
          }
        }}
        title="Delete Module"
        closeLabel="Close"
      >
        Deleting will remove all content from this module and cannot be undone.
        Are you sure you want to delete this module?
        <br />
        <Button
          type="danger"
          onClick={() => handleDeleteModule(elearningData.id)}
          isLoading={deleteElearningStatus.isLoading}
          isDisabled={deleteElearningStatus.isLoading}
          loadingTitle="Deleting..."
        >
          Delete
        </Button>
      </Modal>
      <CannotDeleteModuleModal
        isOpen={blockedDeletion !== null}
        onClose={() => setBlockedDeletion(null)}
        courses={blockedDeletion?.courses ?? []}
        coursesUnavailable={blockedDeletion?.coursesUnavailable ?? false}
      />
      <Modal
        id="DuplicateModuleDialog"
        type="small"
        isOpened={isDuplicateModalOpen}
        closeModal={() => setIsDuplicateModalOpen(false)}
        title="Duplicate module"
        closeLabel="Close"
      >
        <div className="duplicate-module__content">
          {isDuplicateElearningLoading ? (
            <div className="duplicate-module__loading">
              You will be redirected to the new module once it is ready.
              <Spinner size="xl" ariaLabel="Duplicating in progress" />
            </div>
          ) : (
            <>
              Duplicating will copy module and quiz contents into a new module.
              Are you sure you want to replicate this module?
              <br />
              <Button
                type="primary"
                className="duplicate-module__button"
                onClick={() => handleDuplicateModule(elearningData.id)}
              >
                Continue
              </Button>
            </>
          )}
        </div>
      </Modal>
      {isExportToDocumentEnabled && (
        <ExportModuleDialog
          isOpened={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          isBusy={isExportDocumentBusy}
          onBusyChange={setIsExportDocumentBusy}
          elearningData={elearningData}
          canExportModuleToWordDocument={canExportModuleToWordDocument}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="center-form">
        <ContainerForm maxColumns={1}>
          <FormContainer maxColumns={2} className="mb-4">
            <FormItem
              label="Module Name *"
              message={errors.name && errors.name?.message}
              status={errors.name ? 'error' : 'default'}
              isBlock={true}
            >
              <Input
                {...register('name', {})}
                onChange={(e) => handleValueTargetChange(e, 'name')}
                value={watch('name')}
                ariaLabel="name"
                id="elearning-name"
              />
            </FormItem>
            {isElmoAdmin && (
              <FormItem
                label="Description"
                message={errors.description?.message}
                status={errors.description ? 'error' : 'default'}
                isBlock={true}
              >
                <TextArea
                  {...register('description', {})}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleValueTargetChange(e, 'description')
                  }
                  value={watch('description')}
                  ariaLabel="description"
                  id="elearning-description"
                />
              </FormItem>
            )}

            <FormItem
              label="Theme"
              message={errors.theme?.id?.message}
              status={errors.theme?.id ? 'error' : 'default'}
              isBlock={true}
            >
              <Select
                name="theme.id"
                value={getSelectedThemeValue(watchThemeId)}
                closeMenuOnSelect={true}
                placeholder={
                  elearningData.useDefaultTheme
                    ? getDefaultThemeOption().label
                    : getElearningThemeOption({ elearningData }).label
                }
                onChange={handleThemeChange}
                options={[
                  ...(defaultTheme ? [getDefaultThemeOption()] : []),
                  ...themesList,
                ]}
                onMenuScrollToBottom={() => {
                  if (pageTheme <= themesList.length) {
                    setPageTheme(pageTheme + 1);
                  }
                }}
                id="elearning-theme"
                ariaLabel="theme"
              />
            </FormItem>

            {isElaCourseLibraryAdminEnabled && (
              <FormItem
                label="Category"
                message={
                  Array.isArray(errors.categories)
                    ? errors.categories
                        .map((err) => err?.message)
                        .filter(Boolean)
                        .join(', ')
                    : undefined
                }
                status={errors.categories ? 'error' : 'default'}
                isBlock={true}
              >
                <Select
                  name="category.id"
                  isMulti
                  hideSelectedOptions
                  value={watchCategories}
                  closeMenuOnSelect={true}
                  onChange={handleCategoryChange}
                  options={[...categoriesList]}
                  onMenuScrollToBottom={() => {
                    if (pageCategory < (elaCategories?.meta?.pageCount || 0)) {
                      setPageCategory(pageCategory + 1);
                    }
                  }}
                  onInputChange={(value) => handleCategorySearch(value)}
                  id="elearning-category"
                  ariaLabel="categories"
                />
              </FormItem>
            )}
          </FormContainer>
          <Divider />
          <FormContainer maxColumns={2} className="mt-5 mb-4">
            <FormItem
              label="Title Location"
              message={errors.titleMetadata?.posX?.message}
              status={errors.titleMetadata?.posX ? 'error' : 'default'}
              size="sm"
            >
              <Input
                {...register('titleMetadata.posX', {})}
                onChange={(e) =>
                  handleValueTargetChange(e, 'titleMetadata.posX')
                }
                value={
                  watch('titleMetadata.posX') ? watch('titleMetadata.posX') : ''
                }
                ariaLabel="Title position X"
                inputAdornmentEnd="px"
                id="elearning-title-posX"
              />
            </FormItem>

            <FormItem
              message={errors.titleMetadata?.posY?.message}
              status={errors.titleMetadata?.posY ? 'error' : 'default'}
              size="sm"
              label="&nbsp;"
            >
              <Input
                {...register('titleMetadata.posY', {})}
                onChange={(e) =>
                  handleValueTargetChange(e, 'titleMetadata.posY')
                }
                value={
                  watch('titleMetadata.posY') ? watch('titleMetadata.posY') : ''
                }
                ariaLabel="Title position Y"
                inputAdornmentEnd="px"
                id="elearning-title-posY"
              />
            </FormItem>

            <FormItem
              label="Title Font"
              message={errors.titleMetadata?.nameFont?.message}
              size="sm"
              status={errors.titleMetadata?.nameFont ? 'error' : 'default'}
            >
              <NativeSelect
                {...register('titleMetadata.nameFont', {})}
                value={watch('titleMetadata.nameFont')}
                onChange={(e) => handleValueChange(e, 'titleMetadata.nameFont')}
                id="elearning-title-nameFont"
                ariaLabel="Title Font"
              >
                {titleFontOptions.map(
                  (font: TitleFontOptionsType, index: number) => (
                    <NativeSelect.Option
                      label={font.name}
                      value={font.value}
                      key={index}
                    />
                  ),
                )}
              </NativeSelect>
            </FormItem>

            <FormItem
              label="Title Size "
              message={errors.titleMetadata?.nameSize?.message}
              status={errors.titleMetadata?.nameSize ? 'error' : 'default'}
              size="sm"
            >
              <Input
                {...register('titleMetadata.nameSize', {})}
                onChange={(e) =>
                  handleValueTargetChange(e, 'titleMetadata.nameSize')
                }
                value={watch('titleMetadata.nameSize')}
                ariaLabel="Title Size"
                id="elearning-title-nameFont"
              />
            </FormItem>

            <FormItem
              label="Title Font Colour"
              message={errors.titleMetadata?.nameColor?.message}
              status={errors.titleMetadata?.nameColor ? 'error' : 'default'}
              size="sm"
            >
              <ColourPicker
                defaultColor={watch('titleMetadata.nameColor')}
                handleChange={(e) =>
                  handleValueChange(e, 'titleMetadata.nameColor')
                }
                register={register}
                registerName="titleMetadata.nameColor"
                isFloat={true}
                watch={watch}
              />
            </FormItem>
          </FormContainer>
          <Divider />
          <FormContainer maxColumns={2} className="mt-5">
            <FormItem
              label="Show Menu"
              message={errors.playerMetadata?.showMenu?.message}
              status={errors.playerMetadata?.showMenu ? 'error' : 'default'}
              size="sm"
            >
              <NativeSelect
                value={watch('playerMetadata.showMenu')}
                {...register('playerMetadata.showMenu', {})}
                onChange={(e) =>
                  handleValueChange(e, 'playerMetadata.showMenu')
                }
                ariaLabel="Show Menu"
                id="elearning-showMenu"
              >
                <NativeSelect.Option label="No" value="0" />
                <NativeSelect.Option label="Yes" value="1" />
              </NativeSelect>
            </FormItem>

            <FormItem
              label="Mute Voiceover"
              message={errors.playerMetadata?.mute?.message}
              status={errors.playerMetadata?.mute ? 'error' : 'default'}
              size="sm"
            >
              <NativeSelect
                value={watch('playerMetadata.mute')}
                {...register('playerMetadata.mute', {
                  value: +elearningData.playerMetadata.mute,
                })}
                ariaLabel="Mute Voiceover"
                onChange={(e) => handleValueChange(e, 'playerMetadata.mute')}
                id="elearning-muteVoiceover"
              >
                {muteTypes.map((muteType) => (
                  <NativeSelect.Option
                    key={muteType.label}
                    label={muteType.label}
                    value={muteType.value}
                  />
                ))}
              </NativeSelect>
            </FormItem>

            <FormItem
              label="Default Background Image"
              message={errors.bgImageName?.message}
              status={errors.bgImageName ? 'error' : 'default'}
              size="sm"
            >
              <CKFileUpload
                connectorDetails={getCkFinderConfig({
                  type: getCkFileType([CkFinderResourceTypeEnum.IMAGES]),
                  tenantId: elearningData.tenantId ?? '',
                  entityId: elearningData.id,
                  folderName: 'elearnings',
                })}
                defaultFileName={getValues('bgImageName') ?? ''}
                fileType="images"
                handlerFunction={{ onFileChoose: handleImgUpload }}
                register={register}
                registerName="bgImageName"
              />
            </FormItem>
          </FormContainer>

          <Row className="mt-4" justifyContent="start" isNoGutters={true}>
            <Col isAuto={true} grow={0}>
              <Button
                type="primary"
                htmlType="submit"
                ariaLabel="Submit Form"
                className="px-5"
                id="module-submit-button"
              >
                Save
              </Button>
            </Col>
            <Col isAuto={true}>
              <Popover key={moreMenuKey} mode="click" isNoPadding={true}>
                <Popover.Target>
                  <Button
                    type="default"
                    htmlType="button"
                    ariaLabel="Others"
                    className="ml-2"
                  >
                    <MoreHorizIcon />
                  </Button>
                </Popover.Target>
                <Popover.Content>
                  <Button
                    type="default"
                    htmlType="button"
                    ariaLabel="Manage Assets"
                    isText={true}
                    isUppercase={false}
                    icon={<FileIcon />}
                    onClick={() => {
                      handleManageAssets();
                      closeMoreMenu();
                    }}
                  >
                    Manage Assets
                  </Button>
                  {isExportToDocumentEnabled && (
                    <Button
                      type="default"
                      htmlType="button"
                      ariaLabel="Export Module to word document"
                      isText={true}
                      isUppercase={false}
                      icon={<DescriptionOutlinedIcon />}
                      onClick={() => {
                        setIsExportModalOpen(isExportToDocumentEnabled);
                        closeMoreMenu();
                      }}
                    >
                      Export to document
                    </Button>
                  )}
                  <Button
                    type="default"
                    htmlType="button"
                    ariaLabel="Duplicate Module"
                    isText={true}
                    isUppercase={false}
                    icon={<CopyIcon />}
                    onClick={() => {
                      setIsDuplicateModalOpen(true);
                      closeMoreMenu();
                    }}
                  >
                    Duplicate Module
                  </Button>
                  {canDelete && (
                    <Button
                      type="default"
                      htmlType="button"
                      ariaLabel="Delete Module"
                      onClick={() => {
                        setIsModalOpen(true);
                        closeMoreMenu();
                      }}
                      isText={true}
                      isUppercase={false}
                      icon={<DeleteIcon />}
                    >
                      Delete Module
                    </Button>
                  )}
                </Popover.Content>
              </Popover>
            </Col>
          </Row>
        </ContainerForm>
      </form>
    </>
  );
};
